import { h } from 'preact'
import { useRef, useState, useEffect } from 'preact/hooks'
import { batch } from '@preact/signals'
import { chatApi } from '../chat-api.js'
import {
  optimisticReactionToggle,
  reactionHoverLabel,
  reactionsForViewer,
  reactorEntries,
} from '../reaction-summary.js'

const PRIMARY_REACTION = '❤️'
const QUICK_REACTIONS  = ['❤️', '👍', '😂', '😮', '😢', '🙏']

function formatReactedAt(iso) {
  if (!iso) return ''
  const d   = new Date(iso)
  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`

  const isToday =
    d.getDate()     === now.getDate()     &&
    d.getMonth()    === now.getMonth()    &&
    d.getFullYear() === now.getFullYear()

  if (isToday) return time

  const isSameYear = d.getFullYear() === now.getFullYear()

  const dateOpts = isSameYear
    ? { month: 'short', day: 'numeric' }
    : { year: 'numeric', month: 'short', day: 'numeric' }

  return `${d.toLocaleDateString('hu-HU', dateOpts)}, ${time}`
}

/**
 * BubbleReactions — renders reaction chips (inline=true) or the hover action
 * button + emoji picker (inline=false).
 *
 * inline=true  → existing reaction chips below the bubble
 * inline=false → ❤️ quick-react button + emoji picker popup on hover
 *
 * The picker is always in the DOM; CSS opacity/pointer-events show it on
 * .chat-react-wrap:hover so it stays visible while the cursor moves into it.
 * Clicking the heart button directly toggles ❤️ without opening the picker.
 *
 * For inline chips: hover lists every reactor (name + time). Own chip click
 * toggles the reaction; a foreign chip click pins the same list open.
 */
export function BubbleReactions({ message, store, roomId, inline }) {
  const pickerRef      = useRef(null)
  const [openPopover, setOpenPopover] = useState(null)
  const msgId          = message.id

  // reactionsById wins once a live `reaction_updated` arrives; otherwise the
  // REST payload on the message (both include `reactors`).
  const reactions = reactionsForViewer(
    store.reactionsById.value[msgId] ?? message.reactions ?? [],
    store.personId,
  )

  // Close popover on outside click.
  useEffect(() => {
    if (!openPopover) return
    function handleMouseDown(e) {
      if (!e.target.closest('.chat-reaction-chip')) {
        setOpenPopover(null)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [openPopover])

  async function toggleReaction(emoji) {
    if (!msgId || !roomId) return

    const existing = store.reactionsById.value[msgId] ?? message.reactions ?? []
    const optimistic = optimisticReactionToggle(existing, emoji, store.personId, store.personName)

    batch(() => {
      store.reactionsById.value = {
        ...store.reactionsById.value,
        [msgId]: optimistic,
      }
    })

    try {
      await chatApi.reactions.toggle(roomId, msgId, emoji)
    } catch {
      batch(() => {
        store.reactionsById.value = {
          ...store.reactionsById.value,
          [msgId]: existing,
        }
      })
    }
  }

  // ── Inline: reaction chips row ──────────────────────────────
  if (inline) {
    return <InlineReactions
      reactions={reactions}
      toggleReaction={toggleReaction}
      openPopover={openPopover}
      setOpenPopover={setOpenPopover}
    />
  }

  // ── Action button: ❤️ click-to-toggle + hover picker ────────
  const heartReacted = reactions.some(r => r.emoji === PRIMARY_REACTION && r.reacted_by_me)

  return (
    <div class="chat-react-wrap" ref={pickerRef}>
      {/* Click heart → toggle ❤️ directly; hover shows full picker */}
      <button
        type="button"
        class={`chat-msg-action-btn${heartReacted ? ' chat-msg-action-btn--active' : ''}`}
        title="Reakció"
        onClick={e => {
          e.stopPropagation()
          toggleReaction(PRIMARY_REACTION)
        }}
      >
        <i class={`bi bi-heart${heartReacted ? '-fill' : ''}`} />
      </button>

      {/* Picker — always in DOM, shown via CSS :hover on .chat-react-wrap */}
      <div class="chat-react-picker">
        {QUICK_REACTIONS.map(emoji => (
          <button
            key={emoji}
            type="button"
            class="chat-react-picker-btn"
            onClick={e => {
              e.stopPropagation()
              toggleReaction(emoji)
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * InlineReactions — the chips row rendered below a bubble.
 *
 * Two-phase mount: first render uses the base .chat-reactions class
 * (max-height:0, opacity:0), then after a double-rAF the --visible modifier
 * is added so the CSS transition plays and the row grows in smoothly without
 * causing an abrupt layout jump.
 */
function InlineReactions({ reactions, toggleReaction, openPopover, setOpenPopover }) {
  const hasReactions = reactions.length > 0
  const [visible, setVisible] = useState(false)
  const rafRef = useRef(null)

  useEffect(() => {
    if (hasReactions && !visible) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setVisible(true))
      })
    }
    if (!hasReactions) {
      setVisible(false)
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [hasReactions])

  if (!hasReactions && !visible) return null

  return (
    <div class={`chat-reactions${visible ? ' chat-reactions--visible' : ''}`}>
      {reactions.map(r => (
        <button
          key={r.emoji}
          type="button"
          class={`chat-reaction-chip${r.reacted_by_me ? ' chat-reaction-chip--mine' : ''}${openPopover === r.emoji ? ' chat-reaction-chip--details' : ''}`}
          title={reactionHoverLabel(r) || undefined}
          onClick={() => {
            if (r.reacted_by_me) {
              toggleReaction(r.emoji)
            } else {
              setOpenPopover(prev => prev === r.emoji ? null : r.emoji)
            }
          }}
        >
          <span class="chat-reaction-emoji">{r.emoji}</span>
          <span class="chat-reaction-count">{r.count}</span>

          {reactorEntries(r).length > 0 && (
            <div class="chat-reaction-popover" role="tooltip" onClick={e => e.stopPropagation()}>
              {reactorEntries(r).map(reactor => (
                <div key={reactor.person_id} class="chat-reaction-popover-row">
                  <span class="chat-reaction-popover-name">{reactor.name}</span>
                  <span class="chat-reaction-popover-time">{formatReactedAt(reactor.reacted_at)}</span>
                </div>
              ))}
            </div>
          )}
        </button>
      ))}
    </div>
  )
}
